import React, { useEffect, useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout } from '../components/Layout';
import { QuoteWidget } from '../components/QuoteWidget';
import { MarqueeGallery } from '../components/MarqueeGallery';
import { TransformationSlider } from '../components/TransformationSlider';
import { ProcessGrid } from '../components/ProcessGrid';
import { Showroom } from '../components/Showroom';
import { ValueProp } from '../components/ValueProp';
import { FacebookIcon, InstagramIcon, TwitterIcon, PhoneIcon, MailIcon, MapPinIcon, Star, ChevronLeft, ChevronRight } from 'lucide-react';
const HERO_IMAGES = ['https://cdn.magicpatterns.com/uploads/uyAyGkhhVAJn3hZ2c4Gr3n/image.png', 'https://images.unsplash.com/photo-1588854337236-6889d631faa8?auto=format&fit=crop&q=80&w=800', 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&q=80&w=800', 'https://images.pexels.com/photos/1080721/pexels-photo-1080721.jpeg?auto=compress&cs=tinysrgb&w=800', 'https://images.pexels.com/photos/8186477/pexels-photo-8186477.jpeg?auto=compress&cs=tinysrgb&w=800'];
const TESTIMONIALS = [{
  name: 'Jack Smith',
  location: 'Bowie, MD',
  text: 'Nk home improvement has a great team, friendly relation, they designed the ideal house for my needs. it is a fantastic project, we are very happy! they were originally recommended to us by a friend and now we recommend them.',
  image: 'https://i.pravatar.cc/150?u=jack',
  rating: 5
}, {
  name: 'Sarah Johnson',
  location: 'Bethesda, MD',
  text: 'The cabinet refacing was seamless. They transformed our kitchen in just two days without the mess of a full remodel. Highly professional and detail-oriented team.',
  image: 'https://i.pravatar.cc/150?u=sarah',
  rating: 5
}, {
  name: 'Michael Chen',
  location: 'Annapolis, MD',
  text: 'Excellent workmanship on our bathroom flooring. The attention to detail with the tiling was impressive. NK Home Improvement truly delivers on their promise of quality.',
  image: 'https://i.pravatar.cc/150?u=michael',
  rating: 5
}, {
  name: 'Emily Rodriguez',
  location: 'Silver Spring, MD',
  text: 'Zero mess, zero stress. They worked in my home with so much respect. The results exceeded my expectations. My kitchen feels fresh and modern now.',
  image: 'https://i.pravatar.cc/150?u=emily',
  rating: 5
}, {
  name: 'David Thompson',
  location: 'Alexandria, VA',
  text: "Honest recommendations and transparent pricing. They didn't try to upsell me on things I didn't need. The craftsmanship is top-notch.",
  image: 'https://i.pravatar.cc/150?u=david',
  rating: 5
}, {
  name: 'Jennifer Lee',
  location: 'Potomac, MD',
  text: 'The team at NK Home Improvement is simply the best. They refaced our cabinets and the quality is outstanding. It looks like a brand new kitchen!',
  image: 'https://i.pravatar.cc/150?u=jennifer',
  rating: 5
}, {
  name: 'Robert Wilson',
  location: 'Arlington, VA',
  text: 'Highly impressed with the flooring work. They were punctual, professional, and the finish is flawless. Will definitely use them again for future projects.',
  image: 'https://i.pravatar.cc/150?u=robert',
  rating: 5
}, {
  name: 'Lisa Anderson',
  location: 'Rockville, MD',
  text: 'From the initial quote to the final walkthrough, everything was handled with care. Our bathroom looks incredible. Thank you NK Home Improvement!',
  image: 'https://i.pravatar.cc/150?u=lisa',
  rating: 5
}];
const PROJECTS = [{
  title: 'Traditional Oak Reface',
  location: 'Bethesda, MD',
  image: 'https://cdn.magicpatterns.com/uploads/fZacsfF1XsKzt9rhx3HW2f/b-1.jpg'
}, {
  title: 'Modern Kitchen Update',
  location: 'Arlington, VA',
  image: 'https://cdn.magicpatterns.com/uploads/jSif27kDeCfjpi6GVtuyf2/b-2.jpg'
}, {
  title: 'Classic Cabinet Refinish',
  location: 'Potomac, MD',
  image: 'https://cdn.magicpatterns.com/uploads/v1Qn8imjfgNzCQUpAGimmM/b-3.jpg'
}, {
  title: 'Vintage Wood Restoration',
  location: 'Silver Spring, MD',
  image: 'https://cdn.magicpatterns.com/uploads/tfJ5piQs4i3CyZKkHGeSNj/b-4.jpg'
}, {
  title: 'Rustic Kitchen Remodel',
  location: 'Bowie, MD',
  image: 'https://cdn.magicpatterns.com/uploads/iXZhiea3EJJkBE99fJvRUh/b-5.jpg'
}];
export function LandingPage() {
  const [currentImage, setCurrentImage] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(TESTIMONIALS.length / itemsPerPage);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentPage(prev => (prev + 1) % totalPages);
    }, 5000);
    return () => clearInterval(timer);
  }, [totalPages]);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <Layout data-id="element-328">
      {/* Hero Section */}
      <header className="relative h-[60vh] flex items-center overflow-hidden" data-id="element-329">
        {/* Background Image - Darker, Modern Kitchen */}
        <div className="absolute inset-0 z-0" data-id="element-330">
          <img src="https://images.unsplash.com/photo-1556912173-3bb406ef7e77?auto=format&fit=crop&q=80&w=2000" alt="Luxury Modern Kitchen" className="w-full h-full object-cover brightness-[0.6] contrast-110" data-id="element-331" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" data-id="element-332" />
        </div>

        <div className="max-w-7xl mx-auto w-full px-6 relative z-10 text-center" data-id="element-333">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="max-w-5xl mx-auto" data-id="element-334">
            <h1 className="text-4xl md:text-7xl font-black text-white leading-[0.9] tracking-tight" data-id="element-335">
              BEAUTIFULLY CRAFTED, <br data-id="element-336" />
              <span className="text-white/90" data-id="element-337">DESIGNED TO LAST</span>
            </h1>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.5
          }} className="mt-12" data-id="element-338">
              <Link to="/showroom" className="inline-block bg-[#6B0D24] text-white px-12 py-5 font-black uppercase tracking-[0.2em] text-sm hover:bg-[#5A0B1E] transition-all duration-300 shadow-2xl" data-id="element-339">
                View Our Works
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </header>

      <MarqueeGallery data-id="element-340" />

      {/* Welcome Section */}
      <section className="bg-white overflow-hidden" data-id="element-341">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center" data-id="element-342">
          <div className="flex-1 p-6 md:p-8 lg:p-10 space-y-4" data-id="element-343">
            <div className="space-y-1" data-id="element-344">
              <div className="flex items-center gap-3" data-id="element-345">
                <div className="h-px w-10 bg-[#6B0D24]/30" data-id="element-346" />
                <h2 className="text-base md:text-lg font-bold text-[#6B0D24] uppercase tracking-[0.3em]" data-id="element-347">
                  Welcome to
                </h2>
              </div>
              <h3 className="text-3xl md:text-5xl font-black text-charcoal leading-none tracking-tighter" data-id="element-348">
                NK Home <span className="text-[#6B0D24]" data-id="element-349">Improvement</span>
              </h3>
            </div>

            <div className="space-y-3 text-charcoal/80 leading-relaxed text-sm md:text-base" data-id="element-350">
              <p className="text-base md:text-lg font-medium text-charcoal" data-id="element-351">
                High-quality kitchen, bathroom, and flooring transformations. We
                specialize in premium cabinet refacing and refinishing using
                solid wood and plywood—never particle board.
              </p>
              <p data-id="element-352">
                From classic elegance to modern luxury, we deliver expert
                workmanship at the best value for your home or business.
              </p>
            </div>

            <Link to="/about" className="inline-block bg-[#6B0D24] text-white px-8 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#5A0B1E] transition-colors" data-id="element-353">
              Read More
            </Link>
          </div>

          <div className="flex-1 h-[200px] lg:h-[400px] w-full" data-id="element-354">
            <img src="https://cdn.magicpatterns.com/uploads/5vxXZF46MS6oG5oMddRUPw/pexels-curtis-adams-1694007-4030060.jpg" alt="Luxury Kitchen Cabinetry" className="w-full h-full object-cover" data-id="element-355" />
          </div>
        </div>
      </section>

      {/* Happy Clients Section */}
      <section className="relative py-12 overflow-hidden" data-id="element-356">
        {/* Background Image */}
        <div className="absolute inset-0 z-0" data-id="element-357">
          <img src="https://cdn.magicpatterns.com/uploads/mzbLFwnMbvcjEXRfABUGXF/image.png" alt="Happy Clients Background" className="w-full h-full object-cover brightness-[0.4]" data-id="element-358" />
          <div className="absolute inset-0 bg-black/40" data-id="element-359" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-360">
          <div className="text-center mb-10" data-id="element-361">
            <h2 className="text-3xl md:text-5xl font-black text-white leading-none tracking-tighter mb-3 drop-shadow-lg" data-id="element-362">
              CLIENT <span className="text-[#6B0D24]" data-id="element-363">REVIEWS</span>
            </h2>
            <p className="text-white/80 text-base max-w-2xl mx-auto drop-shadow-md" data-id="element-364">
              Don't just take our word for it. Here's what our satisfied
              homeowners have to say about their transformations.
            </p>
          </div>

          <div className="relative" data-id="element-365">
            <AnimatePresence mode="wait" data-id="element-366">
              <motion.div key={currentPage} initial={{
              opacity: 0,
              x: 50
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -50
            }} transition={{
              duration: 0.6,
              ease: 'easeInOut'
            }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-367">
                {TESTIMONIALS.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage).map((testimonial, idx) => <div key={idx} className="bg-white/5 backdrop-blur-md p-6 rounded-xl border border-white/10 flex flex-col justify-between hover:bg-white/10 transition-colors duration-300 min-h-[240px]" data-id="element-368">
                    <div className="space-y-3" data-id="element-369">
                      <div className="flex gap-1" data-id="element-370">
                        {[...Array(testimonial.rating)].map((_, i) => <Star key={i} size={14} className="fill-[#6B0D24] text-[#6B0D24]" data-id="element-371" />)}
                      </div>
                      <p className="text-white/90 text-sm leading-relaxed italic" data-id="element-372">
                        "{testimonial.text}"
                      </p>
                    </div>

                    <div className="mt-6 flex items-center gap-4 border-t border-white/10 pt-4" data-id="element-373">
                      <img src={testimonial.image} alt={testimonial.name} className="w-10 h-10 rounded-full object-cover border-2 border-[#6B0D24]" data-id="element-374" />
                      <div data-id="element-375">
                        <h4 className="text-white font-bold text-xs" data-id="element-376">
                          {testimonial.name}
                        </h4>
                        <p className="text-white/50 text-[10px]" data-id="element-377">
                          {testimonial.location}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </motion.div>
            </AnimatePresence>

            {/* Pagination Dots */}
            <div className="flex justify-center gap-3 mt-8" data-id="element-378">
              {[...Array(totalPages)].map((_, i) => <button key={i} onClick={() => setCurrentPage(i)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${currentPage === i ? 'bg-[#6B0D24] w-6' : 'bg-white/20 hover:bg-white/40'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-379" />)}
            </div>

            {/* Navigation Arrows - Desktop Only */}
            <button onClick={() => setCurrentPage(prev => (prev - 1 + totalPages) % totalPages)} className="absolute -left-16 top-1/2 -translate-y-1/2 hidden xl:flex p-3 rounded-full border border-white/10 hover:bg-white/10 transition-colors text-white" aria-label="Previous testimonials" data-id="element-380">
              <ChevronLeft size={24} data-id="element-381" />
            </button>
            <button onClick={() => setCurrentPage(prev => (prev + 1) % totalPages)} className="absolute -right-16 top-1/2 -translate-y-1/2 hidden xl:flex p-3 rounded-full border border-white/10 hover:bg-white/10 transition-colors text-white" aria-label="Next testimonials" data-id="element-382">
              <ChevronRight size={24} data-id="element-383" />
            </button>
          </div>

          <div className="mt-10 text-center" data-id="element-384">
            <Link to="/testimonials" className="inline-block bg-[#6B0D24] text-white px-10 py-3 font-black uppercase tracking-[0.2em] text-xs hover:bg-[#5A0B1E] transition-all duration-300 shadow-xl" data-id="element-385">
              Read More Stories
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Projects Section */}
      <section className="py-12 px-6 bg-alabaster" data-id="element-386">
        <div className="max-w-7xl mx-auto" data-id="element-387">
          <div className="text-center mb-10 space-y-1" data-id="element-388">
            <span className="text-charcoal/40 text-lg font-light" data-id="element-389">Latest</span>
            <h2 className="text-5xl md:text-7xl font-black text-[#6B0D24] leading-none tracking-tighter" data-id="element-390">
              Projects
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-391">
            {PROJECTS.map((project, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className={`group cursor-pointer ${idx >= 3 ? 'lg:col-span-1 md:col-span-1' : ''}`} data-id="element-392">
                <div className="relative h-[300px] overflow-hidden border border-charcoal/10 bg-white" data-id="element-393">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" data-id="element-394" />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-[#6B0D24]/40 transition-colors duration-500" data-id="element-395" />

                  <div className="absolute bottom-0 left-0 w-full p-8 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500" data-id="element-396">
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] mb-2 text-white/70" data-id="element-397">
                      {project.location}
                    </p>
                    <h3 className="text-2xl font-black uppercase tracking-tight leading-none" data-id="element-398">
                      {project.title}
                    </h3>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <div className="mt-10 text-center" data-id="element-399">
            <Link to="/showroom" className="inline-block border-2 border-[#6B0D24] text-[#6B0D24] px-12 py-4 font-black uppercase tracking-[0.2em] text-xs hover:bg-[#6B0D24] hover:text-white transition-all duration-300" data-id="element-400">
              View All Projects
            </Link>
          </div>
        </div>
      </section>

      <TransformationSlider data-id="element-401" />
    </Layout>;
}